import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Lock, Mail, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin";
  const { refresh } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", {
        email, password, totp_code: totp || undefined,
      });
      if (data.requires_2fa) {
        setNeeds2fa(true);
        toast.message("Enter your 2FA code");
        return;
      }
      await refresh();
      toast.success("Admin session active for 30 minutes");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain-bg px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-between">
          <Logo />
          <div className="text-xs font-mono text-amber-400/80 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> admin
          </div>
        </div>

        <h1 className="font-display text-4xl font-semibold tracking-tight">Admin console</h1>
        <p className="mt-3 text-sm text-zinc-400">Session expires after 30 minutes of inactivity.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {!needs2fa && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Admin email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 bg-zinc-950 border-zinc-800 h-11" required autoFocus data-testid="admin-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 bg-zinc-950 border-zinc-800 h-11" required data-testid="admin-password-input" />
                </div>
              </div>
            </>
          )}

          {needs2fa && (
            <div>
              <div className="flex items-center gap-2 text-sm text-zinc-300 mb-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Two-factor authentication
              </div>
              <Input value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="000000" className="bg-zinc-950 border-zinc-800 h-12 text-center font-mono text-2xl tracking-[0.5em]" autoFocus data-testid="admin-totp-input" />
            </div>
          )}

          <Button type="submit" disabled={busy} className="w-full h-11 bg-white text-zinc-950 hover:bg-zinc-200" data-testid="admin-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Authenticate"}
          </Button>
        </form>
      </div>
    </div>
  );
}
